import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

type SocialProvider = 'google' | 'microsoft'

/** Subset of a better-auth client error we care about for messaging. */
type AuthError = { code?: string; message?: string }

/** Shape returned by the better-auth email/reset client methods. */
type AuthResult = { error?: AuthError | null }

type UseChatbotAuthFormDeps = {
    signInWithEmail: (
        email: string,
        password: string,
        rememberMe?: boolean,
    ) => Promise<AuthResult>
    signUpWithEmail: (params: {
        email: string
        password: string
        name: string
    }) => Promise<AuthResult>
    requestPasswordReset: (
        email: string,
        redirectTo: string,
    ) => Promise<AuthResult>
    signInWithSocialPopup: (
        provider: SocialProvider,
        onAuthSuccess?: () => void | Promise<void>,
    ) => Promise<{ popupBlocked: boolean }>
    /** Refetches the current session (after a successful credential sign-in). */
    refetchSession: () => Promise<unknown>
    /** Reloads the agent access state once the user is authenticated. */
    reload: () => void | Promise<void>
}

/**
 * Shared login-screen logic for the web-component widgets (floating and
 * embedded). Centralizes error mapping, loading state and the sign-in /
 * sign-up / reset / social handlers so both widgets behave identically.
 */
export function useChatbotAuthForm(deps: UseChatbotAuthFormDeps) {
    const { t } = useI18n({ useScope: 'global' })

    const authError = ref<string>()
    /**
     * Non-error feedback shown on a dedicated success screen (e.g. after
     * sign-up or a password-reset request). Kept separate from `authError`
     * so these "check your inbox" messages aren't styled as failures.
     */
    const authSuccess = ref<{ title: string; message: string }>()
    const authLoading = ref(false)

    function mapAuthError(error?: AuthError | null): string {
        const messageByCode: Record<string, string> = {
            INVALID_EMAIL_OR_PASSWORD: t('auth.invalidCredentials'),
            USER_ALREADY_EXISTS: t('auth.emailInUse'),
            EMAIL_NOT_VERIFIED: t('auth.emailNotVerified'),
        }
        return messageByCode[error?.code ?? ''] ?? t('auth.genericError')
    }

    /** Runs an auth action with shared error/loading handling. */
    async function runAuthAction(action: () => Promise<void>): Promise<void> {
        authError.value = undefined
        authSuccess.value = undefined
        authLoading.value = true
        try {
            await action()
        } catch (err) {
            authError.value = mapAuthError(err as AuthError)
        } finally {
            authLoading.value = false
        }
    }

    async function handleSignIn(payload: {
        email: string
        password: string
        rememberMe: boolean
    }): Promise<void> {
        await runAuthAction(async () => {
            const { error } = await deps.signInWithEmail(
                payload.email,
                payload.password,
                payload.rememberMe,
            )
            if (error) {
                authError.value = mapAuthError(error)
                return
            }
            await deps.refetchSession()
            await deps.reload()
        })
    }

    async function handleSignUp(payload: {
        email: string
        password: string
        name: string
    }): Promise<void> {
        await runAuthAction(async () => {
            const { error } = await deps.signUpWithEmail(payload)
            if (error) {
                authError.value = mapAuthError(error)
                return
            }
            // After sign-up the user must verify their email before logging in.
            authSuccess.value = {
                title: t('auth.signUpSuccessTitle'),
                message: t('auth.verifyEmail'),
            }
        })
    }

    async function handleReset(payload: { email: string }): Promise<void> {
        await runAuthAction(async () => {
            const frontendDomain =
                import.meta.env.VITE_FRONTEND_URL ?? 'https://localhost:8080'
            const redirectTo = `${frontendDomain}/auth/reset-password?email=${encodeURIComponent(payload.email)}`
            const { error } = await deps.requestPasswordReset(
                payload.email,
                redirectTo,
            )
            if (error) {
                authError.value = mapAuthError(error)
                return
            }
            authSuccess.value = {
                title: t('auth.resetSentTitle'),
                message: t('auth.resetSent'),
            }
        })
    }

    async function handleSocial(provider: SocialProvider): Promise<void> {
        await runAuthAction(async () => {
            const { popupBlocked } = await deps.signInWithSocialPopup(
                provider,
                () => deps.reload(),
            )
            if (popupBlocked) {
                authError.value = t('auth.popupBlocked')
            }
        })
    }

    return {
        authError,
        authSuccess,
        authLoading,
        handleSignIn,
        handleSignUp,
        handleReset,
        handleSocial,
    }
}
